import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useCountry } from '../../../components/CountrySelector';
import { useReturnSummary } from '../useReturnSummary';
import { UPDATE_RETURN_SUMMARY_MUTATION } from './updateReturnSummaryMutation.rest';

type UpdateReturnSummaryFields = {
  returnMethodId?: number;
  returnOptionId?: number;
  returnReasonHash?: { [id: string]: string };
  selectedItemIds?: string[];
};

export const useUpdateReturnSummaryMutation = (orderNumber: string) => {
  const { country } = useCountry();
  const { returnSummary } = useReturnSummary({ orderNumber });
  const [mutate, { loading, error }] = useMutation(UPDATE_RETURN_SUMMARY_MUTATION);

  const handleUpdate = useCallback(
    (fields: UpdateReturnSummaryFields) => {
      if (!returnSummary)
        return Promise.reject('useUpdateReturnSummaryMutation: return summary not found');

      return mutate({
        variables: {
          id: returnSummary.id,
          input: {
            data: {
              type: 'return_summary',
              id: returnSummary.id,
              attributes: {
                country_code: country.alpha2Code,
                return_method_id: fields.returnMethodId ?? returnSummary.attributes.returnMethodId,
                return_option_id: fields.returnOptionId ?? returnSummary.attributes.returnOptionId,
                return_reason_hash:
                  fields.returnReasonHash ?? returnSummary.attributes.returnReasonHash,
                selected_item_ids:
                  fields.selectedItemIds ?? returnSummary.attributes.selectedItemIds
              }
            }
          }
        }
      });
    },
    [country, mutate, returnSummary]
  );

  return [handleUpdate, { loading, error }] as const;
};
